import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Calendar as CalendarIcon, Clock, Send, Plus, Edit, Trash2,
  Facebook, Instagram, Linkedin, Youtube, Twitter, Music,
  Video, Image, FileText, CheckCircle, XCircle, Loader2,
  ChevronLeft, ChevronRight, Settings, BarChart3, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import api from '@/lib/api';

// Icônes des réseaux sociaux
const NETWORK_ICONS = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  twitter: Twitter,
  tiktok: Music
};

const NETWORK_COLORS = {
  facebook: '#1877F2',
  instagram: '#E4405F',
  linkedin: '#0A66C2',
  youtube: '#FF0000',
  twitter: '#000000',
  tiktok: '#000000'
};

// Composant pour afficher un réseau social
const NetworkBadge = ({ network, selected, onToggle }) => {
  const Icon = NETWORK_ICONS[network] || FileText;
  const color = NETWORK_COLORS[network] || '#666';
  
  return (
    <button
      onClick={() => onToggle(network)}
      className={`flex items-center gap-2 px-3 py-2 rounded-full border-2 transition-all ${
        selected 
          ? 'border-current bg-opacity-10' 
          : 'border-gray-200 hover:border-gray-300'
      }`}
      style={{ borderColor: selected ? color : undefined, color: selected ? color : '#666' }}
    >
      <Icon className="w-4 h-4" />
      <span className="text-sm font-medium capitalize">{network}</span>
    </button>
  );
};

// Composant de publication programmée
const PublicationCard = ({ publication, onEdit, onDelete, onPublish }) => {
  const statusColors = {
    'programmee': 'bg-blue-500',
    'en_cours': 'bg-yellow-500',
    'publiee': 'bg-green-500',
    'erreur': 'bg-red-500'
  };
  
  const statusLabels = {
    'programmee': 'Programmée',
    'en_cours': 'En cours...',
    'publiee': 'Publiée',
    'erreur': 'Erreur'
  };
  
  const date = parseISO(publication.date_publication);
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">
              {format(date, 'dd MMM yyyy', { locale: fr })}
            </span>
            <Clock className="w-4 h-4 text-muted-foreground ml-2" />
            <span className="text-sm">
              {format(date, 'HH:mm')}
            </span>
          </div>
          <Badge className={statusColors[publication.statut]}>
            {statusLabels[publication.statut]}
          </Badge>
        </div>
        
        {/* Réseaux */}
        <div className="flex flex-wrap gap-1 mb-3">
          {publication.plateformes.map((platform) => {
            const Icon = NETWORK_ICONS[platform] || FileText;
            return (
              <div 
                key={platform}
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ backgroundColor: NETWORK_COLORS[platform] || '#666' }}
              >
                <Icon className="w-3 h-3 text-white" />
              </div>
            );
          })}
        </div>
        
        {/* Texte */}
        <p className="text-sm line-clamp-2 mb-3">{publication.texte_post}</p>
        
        {/* Hashtags */}
        {publication.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {publication.hashtags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-blue-500">#{tag}</span>
            ))}
            {publication.hashtags.length > 3 && (
              <span className="text-xs text-muted-foreground">+{publication.hashtags.length - 3}</span>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-2 mt-3 pt-3 border-t">
          {publication.statut === 'programmee' && (
            <>
              <Button variant="outline" size="sm" onClick={() => onPublish(publication.id)}>
                <Send className="w-3 h-3 mr-1" />
                Publier
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEdit(publication)}>
                <Edit className="w-3 h-3" />
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => onDelete(publication.id)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
          {publication.statut === 'publiee' && publication.resultats && (
            <div className="flex gap-1">
              {Object.entries(publication.resultats).map(([platform, result]) => (
                <a 
                  key={platform}
                  href={result.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:underline"
                >
                  Voir sur {platform}
                </a>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Page principale du calendrier
export default function CalendrierPublicationPage() {
  const [publications, setPublications] = useState([]);
  const [connexions, setConnexions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { locale: fr }));
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [editingPublication, setEditingPublication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [videos, setVideos] = useState([]);
  
  // Formulaire nouvelle publication
  const [newPublication, setNewPublication] = useState({
    video_id: '',
    plateformes: [],
    date_publication: '',
    heure_publication: '12:00',
    texte_post: '',
    hashtags: '',
    reel: false,
    story: false
  });

  // Charger les données
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pubRes, connRes, statsRes, videosRes] = await Promise.all([
        api.get('/social-publish/calendrier'),
        api.get('/social-publish/connexions'),
        api.get('/social-publish/stats'),
        api.get('/studio-ia/videos')
      ]);
      
      setPublications(pubRes.data.publications || []);
      setConnexions(connRes.data.connexions || []);
      setStats(statsRes.data);
      setVideos(videosRes.data.videos?.filter(v => v.statut === 'termine') || []);
    } catch (e) {
      console.log('Erreur chargement:', e);
    } finally {
      setLoading(false);
    }
  };

  // Jours de la semaine
  const weekDays = eachDayOfInterval({
    start: weekStart,
    end: endOfWeek(weekStart, { locale: fr })
  });

  // Publications par jour
  const getPublicationsForDay = (date) => {
    return publications.filter(p => 
      isSameDay(parseISO(p.date_publication), date)
    );
  };

  // Navigation semaine
  const previousWeek = () => setWeekStart(addDays(weekStart, -7));
  const nextWeek = () => setWeekStart(addDays(weekStart, 7));

  // Toggle plateforme
  const togglePlateforme = (platform) => {
    setNewPublication(prev => ({
      ...prev,
      plateformes: prev.plateformes.includes(platform)
        ? prev.plateformes.filter(p => p !== platform)
        : [...prev.plateformes, platform]
    }));
  };

  // Créer/modifier publication
  const handleSubmit = async () => {
    if (newPublication.plateformes.length === 0) {
      toast.error('Sélectionnez au moins une plateforme');
      return;
    }
    if (!newPublication.texte_post) {
      toast.error('Ajoutez un texte pour le post');
      return;
    }
    
    setLoading(true);
    try {
      const dateTime = `${format(selectedDate, 'yyyy-MM-dd')}T${newPublication.heure_publication}:00`;
      
      const payload = {
        video_id: newPublication.video_id || null,
        plateformes: newPublication.plateformes,
        date_publication: dateTime,
        texte_post: newPublication.texte_post,
        hashtags: newPublication.hashtags.split(' ').filter(h => h).map(h => h.replace('#', '')),
        reel: newPublication.reel,
        story: newPublication.story
      };
      
      if (editingPublication) {
        await api.put(`/social-publish/calendrier/${editingPublication.id}`, payload);
        toast.success('Publication modifiée');
      } else {
        await api.post('/social-publish/calendrier', payload);
        toast.success('Publication programmée !');
      }
      
      setShowNewDialog(false);
      setEditingPublication(null);
      resetForm();
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewPublication({
      video_id: '',
      plateformes: [],
      date_publication: '',
      heure_publication: '12:00',
      texte_post: '',
      hashtags: '',
      reel: false,
      story: false
    });
  };

  const handleEdit = (publication) => {
    const date = parseISO(publication.date_publication);
    setSelectedDate(date);
    setNewPublication({
      video_id: publication.video_id || '',
      plateformes: publication.plateformes,
      date_publication: publication.date_publication,
      heure_publication: format(date, 'HH:mm'),
      texte_post: publication.texte_post,
      hashtags: publication.hashtags?.join(' ') || '',
      reel: publication.reel || false,
      story: publication.story || false
    });
    setEditingPublication(publication);
    setShowNewDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      await api.delete(`/social-publish/calendrier/${id}`);
      toast.success('Publication supprimée');
      loadData();
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/social-publish/publier-maintenant/${id}`);
      toast.success('Publication en cours...');
      setTimeout(loadData, 3000);
    } catch (e) {
      toast.error('Erreur publication');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Calendrier de Publication</h1>
          <p className="text-muted-foreground">Programmez vos posts sur tous les réseaux sociaux</p>
        </div>
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-pink-500 to-purple-500">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Publication
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPublication ? 'Modifier la publication' : 'Programmer une publication'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Sélection vidéo */}
              {videos.length > 0 && (
                <div className="space-y-2">
                  <Label>Vidéo à publier (optionnel)</Label>
                  <Select 
                    value={newPublication.video_id} 
                    onValueChange={(v) => setNewPublication({...newPublication, video_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner une vidéo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Aucune vidéo</SelectItem>
                      {videos.map((video) => (
                        <SelectItem key={video.id} value={video.id}>
                          <div className="flex items-center gap-2">
                            <Video className="w-4 h-4" />
                            {video.sujet?.slice(0, 40)}...
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Plateformes */}
              <div className="space-y-2">
                <Label>Plateformes</Label>
                <div className="flex flex-wrap gap-2">
                  {['facebook', 'instagram', 'linkedin', 'tiktok', 'youtube', 'twitter'].map((platform) => (
                    <NetworkBadge
                      key={platform}
                      network={platform}
                      selected={newPublication.plateformes.includes(platform)}
                      onToggle={togglePlateforme}
                    />
                  ))}
                </div>
                {connexions.length === 0 && (
                  <p className="text-xs text-amber-600">
                    Connectez vos comptes dans Paramètres pour publier
                  </p>
                )}
              </div>

              {/* Date et heure */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {format(selectedDate, 'dd MMMM yyyy', { locale: fr })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        locale={fr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Heure</Label>
                  <Select 
                    value={newPublication.heure_publication}
                    onValueChange={(v) => setNewPublication({...newPublication, heure_publication: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                          {`${i.toString().padStart(2, '0')}:00`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Texte */}
              <div className="space-y-2">
                <Label>Texte du post</Label>
                <Textarea
                  value={newPublication.texte_post}
                  onChange={(e) => setNewPublication({...newPublication, texte_post: e.target.value})}
                  placeholder="Écrivez votre message..."
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  {newPublication.texte_post.length} caractères
                </p>
              </div>

              {/* Hashtags */}
              <div className="space-y-2">
                <Label>Hashtags</Label>
                <Input
                  value={newPublication.hashtags}
                  onChange={(e) => setNewPublication({...newPublication, hashtags: e.target.value})}
                  placeholder="#agriculture #innovation #terroir"
                />
              </div>

              {/* Options */}
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="reel"
                    checked={newPublication.reel}
                    onCheckedChange={(c) => setNewPublication({...newPublication, reel: c})}
                  />
                  <Label htmlFor="reel">Publier en Reel</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="story"
                    checked={newPublication.story}
                    onCheckedChange={(c) => setNewPublication({...newPublication, story: c})}
                  />
                  <Label htmlFor="story">Publier en Story</Label>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowNewDialog(false); resetForm(); }}>
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingPublication ? 'Modifier' : 'Programmer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-blue-500">{stats.programmees}</p>
              <p className="text-sm text-muted-foreground">Programmées</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-500">{stats.publiees}</p>
              <p className="text-sm text-muted-foreground">Publiées</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-500">{stats.erreurs}</p>
              <p className="text-sm text-muted-foreground">Erreurs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendrier semaine */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle>Semaine du {format(weekStart, 'dd MMMM', { locale: fr })}</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="icon" onClick={previousWeek}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date(), { locale: fr }))}>
                Aujourd'hui
              </Button>
              <Button variant="outline" size="icon" onClick={nextWeek}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {weekDays.map((day) => {
              const dayPublications = getPublicationsForDay(day);
              const isToday = isSameDay(day, new Date());
              
              return (
                <div 
                  key={day.toISOString()} 
                  className={`min-h-[150px] border rounded-lg p-2 ${
                    isToday ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/20' : ''
                  }`}
                >
                  <div className="text-center mb-2">
                    <p className="text-xs text-muted-foreground">
                      {format(day, 'EEE', { locale: fr })}
                    </p>
                    <p className={`text-lg font-bold ${isToday ? 'text-blue-500' : ''}`}>
                      {format(day, 'd')}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    {dayPublications.slice(0, 3).map((pub) => (
                      <div 
                        key={pub.id}
                        className="text-xs p-1 rounded bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-950/30 dark:to-purple-950/30 cursor-pointer hover:opacity-80"
                        onClick={() => handleEdit(pub)}
                      >
                        <div className="flex items-center gap-1">
                          {pub.plateformes.slice(0, 2).map((p) => {
                            const Icon = NETWORK_ICONS[p];
                            return Icon ? <Icon key={p} className="w-3 h-3" /> : null;
                          })}
                          <span className="truncate">{format(parseISO(pub.date_publication), 'HH:mm')}</span>
                        </div>
                      </div>
                    ))}
                    {dayPublications.length > 3 && (
                      <p className="text-xs text-center text-muted-foreground">
                        +{dayPublications.length - 3}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Liste des publications */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {publications
          .filter(p => p.statut !== 'publiee')
          .slice(0, 6)
          .map((pub) => (
            <PublicationCard
              key={pub.id}
              publication={pub}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPublish={handlePublish}
            />
          ))}
      </div>

      {publications.length === 0 && !loading && (
        <Card className="p-8 text-center">
          <CalendarIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg font-medium mb-2">Aucune publication programmée</p>
          <p className="text-muted-foreground mb-4">Créez votre première publication pour commencer</p>
          <Button onClick={() => setShowNewDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Programmer une publication
          </Button>
        </Card>
      )}
    </div>
  );
}
